import SpineWrapper from '@Core/Animation/SpineWrapper';
import { IGeneratedWinlineConfig } from '@Core/SymbolsHighlighter/GeneratedWinlines';
import { ISubLevel } from '@GFTypes/GameStoreTypes';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { IOdometerConfig } from './Elements/Odometer';
import { TParticleEmitterConfig } from './Elements/ParticleEmitter';
import { EWinPresentationStates } from './States/WinPresentationStates';
import { IWinMultiplierPresentationConfig } from './WinMultiplierPresentation';

export enum EWinSuffixes {
    IN = 'IN',
    OUT = 'OUT'
}

export interface IWinConfiguration {
    useGeneratedWinlines: boolean;
    generatedWinlineConfig: IGeneratedWinlineConfig;
    highlightAllWinningSymbols: boolean;
    odometer?: IOdometerConfig;
    particleEmitters?: TParticleEmitterConfig[];
    winMultiplierPresentation: IWinMultiplierPresentationConfig;
}

export enum EWaitFor {
    WIN_PRESENTATION_START = 'WIN_PRESENTATION_START',
    ANIMATION = 'ANIMATION',
    ODOMETER = 'ODOMETER',
    TIMER = 'TIMER',
    NONE = 'NONE'
}

export interface IWinPresentationConfig {
    winPresentationSubLevels?: ISubLevel[];
    winPresentationStepsConfig?: IWinPresentationStepsConfig;
    spine?: SpineWrapper;
    odometerHoldDuration?: number;
    odometerSlotName?: string;
    odometerCheckpoints?: IOdometerCheckpoint[];
}

interface IBaseWinPresentationStep {
    waitFor: EWaitFor;
    skippable: boolean;
    stopSkipping?: boolean;
}

export interface IOdometerCheckpoint {
    value: number;
    eventToEmit: EGameEvents;
    waitForEvent: EGameEvents;
}

export interface IWinPresentationStep extends IBaseWinPresentationStep {
    state: EWinPresentationStates;
    eventToEmit?: EGameEvents;
    odometer?: {
        tween?: {
            targetValue: number;
            duration: number;
            checkpoints?: IOdometerCheckpoint[];
        };
        font?: { key: string; size: number };
    };
    timer?: { duration: number };
    particleEmitterConfigs?: TParticleEmitterConfig[];
}

export type TWinPresentationAnimationRelatedStates =
    | EWinPresentationStates.WIN_PRESENTATION_IN_STATE
    | EWinPresentationStates.WIN_PRESENTATION_IDLE_STATE
    | EWinPresentationStates.WIN_PRESENTATION_OUT_STATE;

export type IWinPresentationSublevelConfig = {
    [K in TWinPresentationAnimationRelatedStates]?: IWinPresentationStepConfig;
};

export type TWinPresentationSublevelsConfig = Record<string, IWinPresentationSublevelConfig>;

type TBaseWinPresentationSteps = {
    [K in Exclude<EWinPresentationStates, TWinPresentationAnimationRelatedStates>]: IWinPresentationStepConfig;
};

export type IWinPresentationStepsConfig = TBaseWinPresentationSteps & {
    sublevels: TWinPresentationSublevelsConfig;
};

interface IWinPresentationStepConfig extends IBaseWinPresentationStep {
    odometer?: { tween?: boolean; font?: boolean };
    timer?: boolean;
    particleEmitter?: { start?: boolean; stop?: boolean };
    eventToEmit?: EGameEvents;
}

export interface IWinPresentationStepExecutor {
    execute(step: IWinPresentationStep): Promise<void>;
}
